import React, { useMemo } from "react";
import { Dna, Activity, Thermometer, Zap, Gauge, Sparkles, Shield, Trophy, DollarSign } from "lucide-react";
import { useVehicle } from "../../../lib/vehicleContext";
import { warrantyFor } from "../../../lib/brand";
import { SohHistoryCard } from "../SohHistoryCard";
import { getClimate } from "../../../lib/climateData";
import { VEHICLE_USAGE_HINTS } from "./_shared";
import {
  capacityRetention,
  degradationVelocity,
  thermalStressIndex,
  chargingQualityScore,
  calibrationConfidence,
  batteryStabilityIndex,
  futureHealthForecast,
  batteryRiskScore,
  resaleBatteryValue,
  eviqScore,
  trustScore,
  healthGrade,
  chargingSplit,
  batteryCycles,
  estimatedRemainingLifeYears,
  climateExposure,
  avgStateOfCharge,
  avgDailyMiles,
  predictedRangeLoss3yr,
  rapidDegradationDetected,
  batteryLegacyTimeline,
} from "../../../lib/batteryAnalytics";
import { LowVoltageCard } from "../LowVoltageCard";
import { CollapseSection } from "../CollapseSection";
import {
  PillarSectionHeader,
  StatBigCard,
  StatChipCard,
  MeterBarCard,
  FactorListCard,
  COLOR,
} from "../pillars/_cards";

/**
 * BatteryDNA — the flagship "one-screen, everything" view.
 *
 * Presents the pack's *identity* rather than a chart-heavy dashboard.
 * The nine sub-panels mirror the DNA-strand metaphor from the pitch:
 *   Health · Longevity · Thermal · Charging · Driving · Efficiency ·
 *   Predicted Future · Risk · Fleet Rank · Value
 *
 * Everything below is derived deterministically in `batteryAnalytics.js`
 * and gated behind LiveGate in the parent shell — no mock data leaks.
 */
export const BatteryDNAView = ({ stateCode }) => {
  const { vehicle } = useVehicle();
  const climate = useMemo(() => getClimate(stateCode), [stateCode]);
  const hints = VEHICLE_USAGE_HINTS[vehicle.id] || VEHICLE_USAGE_HINTS["model-3-lr"];

  const soh = capacityRetention(vehicle);
  const vel = degradationVelocity(vehicle);
  const thermal = thermalStressIndex(vehicle, climate);
  const charging = chargingQualityScore(vehicle, hints);
  const calib = calibrationConfidence(vehicle);
  const stability = batteryStabilityIndex(vehicle);
  const forecast = futureHealthForecast(vehicle);
  const risk = batteryRiskScore(vehicle, climate);
  const value = resaleBatteryValue(vehicle);

  // Fleet rank — where this pack lands versus similar-age Teslas.
  // Percentile derived from SoH delta vs the age-adjusted median (2.4 %/yr
  // baseline). No live cohort call yet — anchored to industry median.
  const ageYears = Math.max(0.5, new Date().getFullYear() - (vehicle.year || new Date().getFullYear() - 3));
  const expectedSoh = 100 - 2.4 * ageYears;
  const rankPct = Math.max(3, Math.min(97, Math.round(50 + (soh - expectedSoh) * 6)));

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-dna">
      <PillarSectionHeader
        eyebrow="Battery Health Profile"
        title="Your pack, decoded."
        kicker="Every dimension of your battery on one screen — health, thermal, charging, and financial signature."
        testid="dna-header"
      />

      {/* Hero row — the DNA "signature" */}
      <div
        className="rounded-2xl border p-4 overflow-hidden relative"
        style={{
          borderColor: "rgba(0,229,255,0.28)",
          background: "linear-gradient(135deg, rgba(57,255,20,0.06) 0%, rgba(0,229,255,0.08) 100%)",
        }}
        data-testid="dna-hero"
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center border"
            style={{
              background: "rgba(57,255,20,0.14)",
              borderColor: "rgba(57,255,20,0.45)",
            }}
          >
            <Dna className="w-4 h-4" style={{ color: COLOR.green }} strokeWidth={2.4} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              Signature
            </div>
            <div className="text-sm text-white font-semibold leading-snug">
              {vehicle.year} {vehicle.model}{vehicle.trim ? ` · ${vehicle.trim}` : ""}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-3xl text-white font-semibold leading-none">
              {soh}<span className="font-mono text-[11px] text-zinc-400">%</span>
            </div>
            <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
              capacity
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5">
          <MiniStat label="Risk" value={`${risk.value}`} unit={risk.band} color={risk.band === "Low" ? COLOR.green : risk.band === "Moderate" ? COLOR.amber : COLOR.red} />
          <MiniStat label="Fleet rank" value={`${rankPct}%`} unit="top" color={COLOR.cyan} />
          <MiniStat label="Value" value={`$${(value.now / 1000).toFixed(1)}k`} unit="battery" color={COLOR.green} />
        </div>
      </div>

      {/* Grid — the 9 DNA strands */}
      <CollapseSection id="dna-strands" eyebrow="Nine dimensions" title="Strands" defaultOpen>
      <div className="grid grid-cols-2 gap-3">
        <StatChipCard
          label="Capacity retention"
          value={`${soh}%`}
          trend={`vs ${expectedSoh.toFixed(1)}% expected`}
          color={soh >= expectedSoh ? COLOR.green : COLOR.amber}
          testid="dna-strand-capacity"
        />
        <StatChipCard
          label="Degradation velocity"
          value={`${vel.toFixed(2)}`}
          trend="%/month · stable"
          color={vel < 0.25 ? COLOR.green : COLOR.amber}
          testid="dna-strand-velocity"
        />
      </div>

      <MeterBarCard
        label="Thermal stress index"
        value={thermal}
        suffix="/100"
        desc={thermal < 40 ? "Cells operating in the sweet spot." : thermal < 65 ? "Warm climate — moderate cumulative stress." : "Elevated — precondition before Supercharging."}
        inverse
        testid="dna-strand-thermal"
      />

      <MeterBarCard
        label="Charging quality"
        value={charging}
        suffix="/100"
        desc={charging >= 80 ? "Predominantly L2 home charging — ideal for cell chemistry." : "Reduce DC fast-charging share to move up."}
        testid="dna-strand-charging"
      />

      <div className="grid grid-cols-2 gap-3">
        <StatChipCard
          label="Calibration confidence"
          value={`${calib}%`}
          trend="model certainty"
          color={COLOR.cyan}
          testid="dna-strand-calibration"
        />
        <StatChipCard
          label="Battery stability"
          value={`${stability}/100`}
          trend="slope consistency"
          color={COLOR.cyan}
          testid="dna-strand-stability"
        />
      </div>

      {/* Predicted future strand */}
      <FactorListCard
        header="Predicted future"
        testid="dna-strand-forecast"
        items={[
          { icon: Sparkles, color: COLOR.green, text: `Year 1: ~${forecast.y1}% SoH`, hint: "Projected retention if habits stay steady." },
          { icon: Sparkles, color: COLOR.cyan, text: `Year 3: ~${forecast.y3}% SoH`, hint: forecast.y3 > (vehicle?.warranty?.threshold ?? 70) ? "Well inside warranty floor." : "Approaching the warranty floor — monitor closely." },
          { icon: Sparkles, color: COLOR.amber, text: `Year 5: ~${forecast.y5}% SoH`, hint: "Aging curve flattens after year 3." },
          { icon: Sparkles, color: COLOR.blue, text: `Year 8: ~${forecast.y8}% SoH`, hint: `End of ${vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"} warranty window.` },
        ]}
      />
      <div className="mt-3">
        <SohHistoryCard vehicle={vehicle} />
      </div>
      </CollapseSection>

      {/* Fleet rank + Value strand */}
      <CollapseSection id="dna-value" eyebrow="Market intelligence" title="Value & fleet rank">
      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-xl border p-4"
          style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
          data-testid="dna-strand-fleet-rank"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Trophy className="w-3 h-3" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
              Fleet rank
            </span>
          </div>
          <div className="font-display text-xl text-white font-semibold leading-none">
            Top {100 - rankPct}%
          </div>
          <div className="font-mono text-[10px] text-zinc-400 mt-1">
            of similar-age {vehicle.model}s
          </div>
        </div>
        <div
          className="rounded-xl border p-4"
          style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
          data-testid="dna-strand-value"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <DollarSign className="w-3 h-3" style={{ color: COLOR.green }} strokeWidth={2.4} />
            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
              Battery value
            </span>
          </div>
          <div className="font-display text-xl text-white font-semibold leading-none">
            ${(value.now / 1000).toFixed(1)}k
          </div>
          <div className="font-mono text-[10px] text-zinc-400 mt-1">
            of ${(value.when100 / 1000).toFixed(1)}k when new
          </div>
        </div>
      </div>
      </CollapseSection>

      {/* Low-voltage (12V/16V) battery monitor */}
      <LowVoltageCard vehicle={vehicle} climate={climate} />

      {/* Passport actions */}
      <CollapseSection id="dna-passport" eyebrow="Share & certify" title="Battery Passport">
      <PassportActions vehicle={vehicle} climate={climate} />
      </CollapseSection>
    </div>
  );
};

const MiniStat = ({ label, value, unit, color }) => (
  <div className="text-center">
    <div className="font-display text-lg text-white font-semibold leading-none">{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-wider mt-1" style={{ color: color || COLOR.cyan }}>
      {unit}
    </div>
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
      {label}
    </div>
  </div>
);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function buildSnapshotForActions(vehicle, climate, extras = {}) {
  const risk = batteryRiskScore(vehicle, climate);
  const forecast = futureHealthForecast(vehicle);
  const value = resaleBatteryValue(vehicle);
  const thermal = thermalStressIndex(vehicle, climate);
  const charging = chargingQualityScore(vehicle, VEHICLE_USAGE_HINTS[vehicle.id] || VEHICLE_USAGE_HINTS["model-3-lr"]);
  const calib = calibrationConfidence(vehicle);
  const score = eviqScore(vehicle, climate);
  // EVIQ Battery Passport™ v2 derivations
  const history = extras.history || {};
  const sessions = extras.sessions || [];
  const firmwareHistory = extras.firmwareHistory || [];
  const serviceEntries = extras.serviceEntries || [];
  const trust = trustScore(vehicle, climate, history);
  const grade = healthGrade(trust.total);
  const split = chargingSplit(sessions);
  const cycles = batteryCycles(vehicle, history.battery_cycles_override);
  const life = estimatedRemainingLifeYears(vehicle);
  const exposure = climateExposure(vehicle, climate);
  const avgSoC = avgStateOfCharge(vehicle);
  const dailyMi = avgDailyMiles(vehicle);
  const predLoss = predictedRangeLoss3yr(vehicle);
  const rapid = rapidDegradationDetected(vehicle);
  const legacy = batteryLegacyTimeline({ vehicle, firmwareHistory, serviceEntries, sessions, climate });
  return {
    vin: vehicle.vin || "UNKNOWN",
    year: vehicle.year || new Date().getFullYear() - 3,
    model: vehicle.model || "Model 3",
    trim: vehicle.trim || null,
    soh_pct: vehicle.sohCurrent ?? 92,
    delta_30d_pct: vehicle.delta30d ?? -0.2,
    epa_when_new_mi: vehicle.epaRange || 300,
    pack_kwh: vehicle.packKwh || 75,
    warranty_years: warrantyFor(vehicle).years,
    warranty_miles: warrantyFor(vehicle).miles,
    warranty_threshold_pct: warrantyFor(vehicle).threshold,
    risk_score: risk.value,
    risk_band: risk.band,
    thermal_stress_index: thermal,
    charging_quality_score: charging,
    calibration_confidence_pct: calib,
    fleet_rank_pct: 50,
    battery_value_usd: value.now,
    forecast_3yr_soh: forecast.y3,
    forecast_8yr_soh: forecast.y8,
    eviq_score: score.total,
    eviq_score_grade: score.grade,
    // v2
    trust_score: trust.total,
    trust_score_breakdown: trust.breakdown,
    health_grade: grade,
    remaining_capacity_pct: +Number(vehicle.sohCurrent ?? 92).toFixed(1),
    estimated_remaining_life_years: life,
    fast_charge_pct: split.fast_pct,
    ac_charge_pct: split.ac_pct,
    battery_cycles: cycles,
    avg_soc_pct: avgSoC,
    avg_daily_miles: dailyMi,
    heat_exposure: exposure.heat,
    cold_exposure: exposure.cold,
    rapid_degradation_detected: rapid,
    battery_repaired: !!history.battery_repaired,
    battery_repaired_date: history.battery_repaired_date || null,
    battery_replaced: !!history.battery_replaced,
    battery_replaced_date: history.battery_replaced_date || null,
    current_software_verified: !!vehicle.firmwareVersion,
    current_software_version: vehicle.firmwareVersion || null,
    predicted_range_loss_3yr_pct: predLoss,
    legacy_timeline: legacy,
  };
}

// Fetch the aux inputs needed for a v2 snapshot.
async function fetchPassportExtras(vin) {
  if (!vin || !API_BASE) return {};
  try {
    const [h, s, f, sl] = await Promise.all([
      fetch(`${API_BASE}/api/battery_history/${encodeURIComponent(vin)}`).then((r) => (r.ok ? r.json() : {})),
      fetch(`${API_BASE}/api/charging/${encodeURIComponent(vin)}/sessions?limit=200`).then((r) => (r.ok ? r.json() : { sessions: [] })),
      fetch(`${API_BASE}/api/firmware/${encodeURIComponent(vin)}`).then((r) => (r.ok ? r.json() : { history: [] })),
      fetch(`${API_BASE}/api/service_log/${encodeURIComponent(vin)}`).then((r) => (r.ok ? r.json() : { entries: [] })),
    ]);
    return {
      history: h || {},
      sessions: s?.sessions || [],
      firmwareHistory: f?.history || [],
      serviceEntries: sl?.entries || [],
    };
  } catch (_e) {
    return {};
  }
}

const PassportActions = ({ vehicle, climate }) => {
  const [shareUrl, setShareUrl] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [busyPdf, setBusyPdf] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const generateShare = async () => {
    if (!API_BASE) return;
    setBusy(true);
    try {
      const extras = await fetchPassportExtras(vehicle.vin);
      const res = await fetch(`${API_BASE}/api/battery/passport`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildSnapshotForActions(vehicle, climate, extras)),
      });
      if (res.ok) {
        const data = await res.json();
        setShareUrl(data.share_url);
      }
    } catch (_e) {}
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!API_BASE) return;
    setBusyPdf(true);
    try {
      const extras = await fetchPassportExtras(vehicle.vin);
      const res = await fetch(`${API_BASE}/api/battery/certify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildSnapshotForActions(vehicle, climate, extras)),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `EVIQ-Certified-${(vehicle.vin || "vehicle").slice(-8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (_e) {}
    finally { setBusyPdf(false); }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {}
  };

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{
        borderColor: "rgba(57,255,20,0.35)",
        background: "linear-gradient(90deg, rgba(57,255,20,0.06), rgba(0,229,255,0.04))",
      }}
      data-testid="dna-passport-actions"
    >
      <div className="flex items-center gap-3">
        <img
          src="/eviq_shield_transparent.png"
          alt="EVIQ Digital Battery Passport shield"
          data-testid="dna-passport-shield"
          className="w-14 h-14 shrink-0"
          style={{
            objectFit: "contain",
            // Soft glow lifts the shield off the card background without
            // needing a bounding tile.
            filter: "drop-shadow(0 0 12px rgba(57,255,20,0.35))",
          }}
        />
        <div className="min-w-0 flex-1">
          <div
            className="font-mono text-[9px] uppercase tracking-[0.24em] leading-tight"
            style={{ color: "#39FF14" }}
            data-testid="dna-passport-verified-header"
          >
            EVIQ BATTERY VERIFIED™
          </div>
          <div className="text-sm text-white font-semibold leading-tight mt-1">
            Digital Battery Passport™
          </div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            Tamper-resistant · QR verifiable · shareable at resale
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={generateShare}
          disabled={busy}
          data-testid="dna-share-passport"
          className="rounded-lg border px-3 py-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-white/[0.04] disabled:opacity-60"
          style={{ borderColor: "rgba(0,229,255,0.55)", background: "rgba(0,229,255,0.10)" }}
        >
          {busy ? "Generating…" : "Share passport"}
        </button>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={busyPdf}
          data-testid="dna-download-cert"
          className="rounded-lg border px-3 py-2.5 text-[12px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-60 brand-gradient"
        >
          {busyPdf ? "Building PDF…" : "Download PDF"}
        </button>
      </div>

      {shareUrl && (
        <div
          className="rounded-lg border p-2.5 flex items-center gap-2"
          style={{ borderColor: "rgba(0,229,255,0.30)", background: "rgba(0,229,255,0.06)" }}
          data-testid="dna-share-url-row"
        >
          <div className="min-w-0 flex-1 font-mono text-[10px] text-zinc-100 truncate">
            {shareUrl}
          </div>
          <button
            type="button"
            onClick={copyLink}
            data-testid="dna-share-copy"
            className="shrink-0 rounded-md border px-2 py-1 text-[10px] font-semibold text-white transition-colors hover:bg-white/10"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
          {/* Native share sheet — falls back to a mailto: if navigator.share
              is unavailable (older desktop browsers). Uses the EVIQ Score
              in the share text so social previews lead with the number. */}
          <button
            type="button"
            onClick={async () => {
              const scoreLine = (() => {
                const s = eviqScore(vehicle, climate);
                return s?.total != null ? `EVIQ Score ${s.total}/100 · ${s.grade}` : "My EVIQ Battery Passport";
              })();
              const shareData = {
                title: "My EVIQ Battery Passport",
                text: scoreLine,
                url: shareUrl,
              };
              try {
                if (navigator.share) {
                  await navigator.share(shareData);
                } else {
                  window.location.href = `mailto:?subject=${encodeURIComponent(scoreLine)}&body=${encodeURIComponent(shareUrl)}`;
                }
              } catch { /* user dismissed */ }
            }}
            data-testid="dna-share-native"
            className="shrink-0 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-semibold hover:opacity-90"
          >
            Share score
          </button>
        </div>
      )}
    </div>
  );
};
